import { useEffect, useMemo, useRef, useState } from "react";
import { type ChannelStream, type Device, type LiveGpsUpdate } from "../api";
import { useInViewSet } from "../lib/in-view";
import { dualCameraTiles } from "../lib/presence";
import { clearChannelStartCooldown, dashboardViewerId, requestChannelStart, viewerIdFor } from "../lib/stream-start";
import { resetLiveAttempts, useLiveChannelWatch } from "../lib/live-watch";
import { channelRunState } from "../lib/live-start";
import { DeviceCameraCard } from "./DeviceCameraCard";

/** JT/T 1078 logical channels: 1 = front, 2 = rear. Other numbers stay generic. */
export function channelCameraLabel(channelNo: number): string {
  if (channelNo === 1) return "Front Camera";
  if (channelNo === 2) return "Rear Camera";
  return `Camera ${channelNo}`;
}

function claimKey(deviceId: string, channel: number): string {
  return `${deviceId}:${channel}`;
}

export function CameraGrid({
  devices,
  selectedId,
  onSelect,
  gps,
  gpsSocketConnected,
  refreshNonce = 0,
  pageActive = true,
  suspendDeviceId = null,
  onPlayback,
  liveTripId = null,
  onToggleLiveTrip,
  viewerScope,
}: {
  devices: Device[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  gps: Record<string, LiveGpsUpdate>;
  gpsSocketConnected: boolean;
  refreshNonce?: number;
  pageActive?: boolean;
  suspendDeviceId?: string | null;
  onPlayback?: (device: Device) => void;
  liveTripId?: string | null;
  onToggleLiveTrip?: (deviceId: string) => void;
  /** Separate viewer lease so this grid does not stop another surface's stream. */
  viewerScope?: string;
}) {
  const [promoted, setPromoted] = useState<ReadonlySet<string>>(() => new Set());
  const viewerId = viewerScope ? viewerIdFor(viewerScope) : dashboardViewerId();
  const viewerIdRef = useRef(viewerId);
  viewerIdRef.current = viewerId;
  const selectedRef = useRef<HTMLDivElement | null>(null);
  const { visible, observe } = useInViewSet();
  const targets = useMemo(
    () =>
      pageActive
        ? devices
            .filter((d) => d.id !== suspendDeviceId)
            .flatMap((d) =>
              [1, 2].map((channel) => ({
                deviceId: d.id,
                channel,
                online: d.online,
                startable: visible.has(d.id),
                promoted: promoted.has(d.id),
              })),
            )
        : [],
    [devices, pageActive, suspendDeviceId, visible, promoted],
  );
  const { streams, startAtRef, queue } = useLiveChannelWatch({
    viewerId,
    targets,
    active: pageActive,
    releaseOnHide: true,
    refreshNonce,
  });

  useEffect(() => {
    selectedRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [selectedId]);

  if (devices.length === 0) {
    return <div className="empty-grid">No cameras yet. Add a device to see live video.</div>;
  }

  return (
    <section className="camera-grid" aria-label="Camera grid">
      {devices.map((d) => {
        const info = streams[d.id];
        const now = Date.now();
        const live = dualCameraTiles(d, info).map((channel) => {
          const run = channelRunState({
            deviceOnline: d.online,
            live: channel.live,
            publishing: channel.publishing,
            startedAt: startAtRef.current.get(claimKey(d.id, channel.channel_no)) ?? null,
            now,
            phase: channel.liveStart?.phase,
          });
          const key = claimKey(d.id, channel.channel_no);
          const playHeld = queue.hold.includes(key) && run.state !== "failed" && run.state !== "recovering";
          const queued = d.online && run.state === "idle" && queue.position[d.id] != null && !playHeld;
          const runState: ChannelStream["runState"] = queued ? "queued" : run.state;
          return {
            ...channel,
            liveFailed: run.failed,
            failReason: run.message,
            runState,
            queuePosition: queue.position[d.id],
            playHeld,
          };
        });
        const delaySec = info?.targetDelaySec ?? 2;
        const selected = selectedId === d.id;
        return (
          <div key={d.id} ref={observe(d.id)} data-view-key={d.id}>
            <div ref={selected ? selectedRef : undefined}>
            <DeviceCameraCard
              device={d}
              live={live}
              delaySec={delaySec}
              selected={selected}
              onSelect={() => onSelect(d.id)}
              onPlayChannel={(channel) => {
                if (!d.online) return;
                setPromoted((prev) => {
                  if (prev.has(d.id)) return prev;
                  const next = new Set(prev);
                  next.add(d.id);
                  return next;
                });
                resetLiveAttempts(viewerIdRef.current, d.id, channel);
                clearChannelStartCooldown(d.id, channel, viewerIdRef.current);
              }}
              onRetryChannel={(channel) => {
                if (!d.online) return;
                resetLiveAttempts(viewerIdRef.current, d.id, channel);
                startAtRef.current.set(claimKey(d.id, channel), Date.now());
                clearChannelStartCooldown(d.id, channel, viewerIdRef.current);
                void requestChannelStart(d.id, channel, viewerIdRef.current);
              }}
              gps={gps[d.id] ?? null}
              gpsSocketConnected={gpsSocketConnected}
              recoverNonce={refreshNonce}
              pageActive={pageActive}
              onPlayback={onPlayback ? () => onPlayback(d) : undefined}
              liveTrip={liveTripId === d.id}
              onToggleLiveTrip={onToggleLiveTrip ? () => onToggleLiveTrip(d.id) : undefined}
            />
            </div>
          </div>
        );
      })}
    </section>
  );
}
